// src/components/pages/DeleteAccount.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaTrash, FaExclamationTriangle, FaTimes, 
  FaUserSlash, FaArrowLeft, FaCheck, FaSpinner,
  FaLock, FaShieldAlt
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';
import { deleteAccount } from '../../redux/slices/authSlice';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const [confirmationText, setConfirmationText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  const handleDeleteAccount = async () => {
    if (confirmationText !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion.');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      console.log('🗑️ Dispatching deleteAccount action...');
      const result = await dispatch(deleteAccount()).unwrap();
      console.log('✅ Account deleted successfully:', result);
      setStep(3);
      setIsLoading(false);
      
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (err) {
      console.error('❌ Delete account error:', err);
      setError(err || 'Failed to delete account. Please try again.');
      setIsLoading(false);
    }
  };

  // Success state
  if (step === 3) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.background }}>
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl p-8 text-center"
            style={{ 
              backgroundColor: colors.card,
              border: `2px solid #22C55E`
            }}
          >
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#22C55E20' }}>
              <FaCheck className="text-4xl text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
              Account Deleted Successfully
            </h2>
            <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>
              We're sorry to see you go. Your account has been permanently deleted.
            </p>
            <p className="text-xs" style={{ color: colors.textSecondary + '80' }}>
              Redirecting to home page...
            </p>
            <div className="mt-4 w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: '#22C55E' }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 3 }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
            style={{ color: colors.primary }}
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        </div>

        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-full text-xs font-medium mb-4" style={{ color: '#EF4444' }}>
            <FaUserSlash className="text-[10px]" /> Delete Account
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: colors.text }}>
            Delete Your Account
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            This action is permanent and cannot be undone
          </p>
        </motion.div>

        {/* Error Message */}
        {error && (
          <motion.div 
            className="p-3 rounded-xl mb-4 text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ 
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FECACA'
            }}
          >
            {error}
          </motion.div>
        )}

        {/* Warning Card */}
        <motion.div 
          className="p-4 rounded-xl mb-6 flex items-start gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ 
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA'
          }}
        >
          <FaExclamationTriangle className="text-red-500 text-lg mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-sm text-red-700 dark:text-red-400">⚠️ Warning: Permanent Action</h4>
            <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">
              Deleting your account will permanently remove all your data including:
            </p>
            <ul className="text-xs text-red-600 dark:text-red-300 mt-1 list-disc list-inside">
              <li>Personal profile information (name, email, phone)</li>
              <li>Order history and delivery addresses</li>
              <li>Saved payment methods and preferences</li>
              <li>Tiffin subscriptions and bookings</li>
            </ul>
          </div>
        </motion.div>

        {/* Confirmation */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <label className="text-sm font-medium block mb-2" style={{ color: colors.text }}>
            Type <span className="font-bold text-red-500">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={confirmationText}
            onChange={(e) => {
              setConfirmationText(e.target.value);
              setError('');
            }}
            placeholder="Type DELETE here..."
            className="w-full p-3 rounded-xl text-sm transition-colors text-center font-mono outline-none"
            style={{
              backgroundColor: colors.inputBackground,
              color: colors.text,
              border: `2px solid ${confirmationText === 'DELETE' ? '#22C55E' : colors.border}`,
            }}
            autoFocus
          />
          {confirmationText && confirmationText !== 'DELETE' && (
            <p className="text-xs mt-1 text-red-500">Please type exactly "DELETE"</p>
          )}
          {confirmationText === 'DELETE' && (
            <p className="text-xs mt-1 text-green-500 flex items-center gap-1">
              <FaCheck className="text-xs" /> Confirmed
            </p>
          )}
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          className="flex gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-3 rounded-xl font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: colors.inputBackground,
              color: colors.text,
              border: `1px solid ${colors.border}`
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            disabled={confirmationText !== 'DELETE' || isLoading}
            className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
            style={{
              backgroundColor: confirmationText === 'DELETE' && !isLoading ? '#EF4444' : colors.border,
              cursor: confirmationText === 'DELETE' && !isLoading ? 'pointer' : 'not-allowed',
              opacity: confirmationText === 'DELETE' && !isLoading ? 1 : 0.5
            }}
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin" /> Deleting...
              </>
            ) : (
              <>
                <FaTrash className="text-sm" /> Delete Account
              </>
            )}
          </button>
        </motion.div>

        {/* Security Note */}
        <motion.div 
          className="mt-4 p-3 rounded-xl flex items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ 
            backgroundColor: colors.inputBackground,
            border: `1px solid ${colors.border}`
          }}
        >
          <FaLock className="text-xs" style={{ color: colors.primary }} />
          <p className="text-xs" style={{ color: colors.textSecondary }}>
            Your data will be permanently deleted within 30 days
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default DeleteAccount;